import {
	TRAITS,
	TRAIT_LABELS,
	BASELINE
} from '~/components/Personality/utils/personalityConstants.js'
import {
	mapPersonalityProfile,
	getProfileDisplayWithWrapper,
	getTopTraits,
	getTraitName,
	getTraitWorkplaceSignal,
	getTraitSignalDescription
} from '~/components/Personality/utils/personalityProfileMapper'
import { generateInsightSummary } from '~/components/Personality/utils/personalityInsights'
import {
	analyzePersonalityGap,
	getTraitInterpretation,
	classifyCoverages
} from '~/components/Personality/utils/personalityGapAnalysis'
import { mapGetters } from 'vuex'

const INSIGHT_KEYS = [
	'adaptabilityToChange',
	'collaborationStyle',
	'communicationStyle',
	'conflictResolution',
	'decisionMaking',
	'workEnvironment'
]

const GAP = 0.5
const BORDERLINE = 0.7
const ADEQUATE = 0.7
const STRENGTH = 1.2
const V2_STRENGTH = 1.0

export default {
	props: {
		wheelMode: {
			type: String,
			required: true,
			validator: v => ['applicant', 'job', 'comparison'].includes(v)
		},
		matchData: {
			type: Object,
			default: null,
			required: false
		}
	},
	data() {
		return {
			selectedMode: 'comparison' // comparison | applicant | job
		}
	},
	computed: {
		...mapGetters('user', ['userIsApplicant']),
			usesTraitApproach() {
				// Keep the local fallback for cached API payloads without the backend field.
				const profile =
					this.activePersonalityData?.personality_classification ||
					mapPersonalityProfile(this.personalityBreakdown)

			return profile?.approach === 'TRAIT'
		},
		activePersonalityData() {
			if (this.mode === 'comparison') {
				return null
			}

			if (this.mode === 'job') {
				return this.jobOfferData
			}

			return this.applicantData
		},
		personalityBreakdown() {
			if (!this.activePersonalityData) {
				return null
			}

			return this.buildPersonalityObject(this.activePersonalityData)
		},
		comparisonBreakdowns() {
			if (this.mode !== 'comparison') {
				return null
			}

			const hasApplicant = this.applicantData?.personality?.length
			const hasJob = this.jobOfferData?.personality?.length

			if (!hasApplicant || !hasJob) {
				return null
			}

			return {
				applicant: this.buildPersonalityObject(this.applicantData),
				job: this.buildPersonalityObject(this.jobOfferData)
			}
		},
		applicantBreakdown() {
			return this.comparisonBreakdowns?.applicant || null
		},
		jobBreakdown() {
			return this.comparisonBreakdowns?.job || null
		},
		comparisonGapAnalysis() {
			if (this.mode !== 'comparison') {
				return null
			}

			if (this.matchData?.personality_v2) {
				const v2 = this.matchData.personality_v2

				return {
					normalizedApplicant: v2.normalized_applicant,
					normalizedJob: v2.normalized_job,
					weights: v2.weights,
					coverages: v2.coverages,
					...this.interpretV2Coverages(v2.coverages, v2.weights)
				}
			}

			if (!this.comparisonBreakdowns) {
				return null
			}

			return analyzePersonalityGap(
				this.applicantBreakdown,
				this.jobBreakdown,
				this.t
			)
		},
		comparisonSignals() {
			if (this.mode !== 'comparison') {
				return null
			}

			if (!this.comparisonBreakdowns) {
				return null
			}

			const normalize = breakdown => {
				const normalized = {}

				TRAITS.forEach(trait => {
					normalized[trait] = (breakdown[trait] || 0) / BASELINE
				})

				return normalized
			}

			return {
				applicantSignal: normalize(this.applicantBreakdown),
				jobSignal: normalize(this.jobBreakdown)
			}
		},
		comparisonMatchScore() {
			if (this.matchData?.personality_v2?.score !== undefined) {
				return this.matchData.personality_v2.score
			}

			if (!this.comparisonGapAnalysis) {
				return 0
			}

			const coverages = Object.values(
				this.comparisonGapAnalysis.coverages || {}
			)

			if (!coverages.length) {
				return 0
			}

			const average =
				coverages.reduce((acc, curr) => {
					return acc + Math.min(curr, 1)
				}, 0) / coverages.length

			return Math.round(average * 100)
		},
		comparisonInsightsSummary() {
			if (this.mode !== 'comparison') {
				return null
			}

			if (!this.comparisonGapAnalysis || !this.comparisonSignals) {
				return null
			}

			return generateInsightSummary(
				this.comparisonGapAnalysis,
				this.comparisonSignals.jobSignal,
				this.comparisonSignals.applicantSignal,
				this.comparisonMatchScore,
				this.t
			)
		},
		traitsInCommon() {
			if (!this.applicantBreakdown || !this.jobBreakdown) return []

			return Object.keys(this.jobBreakdown)
				.filter(
					trait =>
						(this.jobBreakdown[trait] ?? 0) > 0 &&
						(this.applicantBreakdown[trait] ?? 0) > 0
				)
				.map(trait => ({
					key: trait,
					name: this.t(`personality_results.trait.${trait}.name`),
					applicant: this.applicantBreakdown[trait] ?? 0,
					job: this.jobBreakdown[trait] ?? 0
				}))
				.sort((a, b) => b.applicant + b.job - (a.applicant + a.job))
		},
			gapsIdentified() {
				// Keep the local fallback for cached V2 payloads without pre-bucketed coverages.
				const buckets =
					this.matchData?.personality_v2?.personality_classification ||
					classifyCoverages(
					this.comparisonGapAnalysis?.coverages,
					this.jobBreakdown || {},
					this.matchData?.personality_v2 ? V2_STRENGTH : STRENGTH
				)

				const decorate = item => ({
				...item,
				name: this.t(`personality_results.trait.${item.key}.name`),
				signal: this.t(
					`personality_results.trait.${item.key}.workplace_signal`
				)
			})

			return {
				gaps: buckets.gaps.map(decorate),
				borderline: buckets.borderline.map(decorate),
				adequate: buckets.adequate.map(decorate),
				strengths: buckets.strengths.map(decorate)
			}
		},
		traitInterpretation() {
			if (this.mode !== 'comparison') {
				return null
			}

			const coverages = this.comparisonGapAnalysis?.coverages

			if (!coverages) {
				return null
			}

			const jobTraits = this.jobBreakdown || {}
			const applicantTraits = this.applicantBreakdown || {}

			const jobRequiredTraits = Object.keys(jobTraits).filter(
				trait => (jobTraits[trait] ?? 0) > 0
			)

			const toInterpret = trait => {
				const coverage = coverages[trait] ?? 0

				const interpretation = getTraitInterpretation(
					trait,
					coverage,
					this.t
				)

				return interpretation
					? {
							...interpretation,
							jobPercent: jobTraits[trait] ?? 0,
							applicantPercent: applicantTraits[trait] ?? 0
					  }
					: null
			}

			const missingCandidates = jobRequiredTraits
				.filter(trait => (applicantTraits[trait] ?? 0) === 0)
				.map(toInterpret)
				.filter(Boolean)

			if (missingCandidates.length) {
				return missingCandidates.sort((a, b) => {
					if (b.jobPercent !== a.jobPercent) {
						return b.jobPercent - a.jobPercent
					}

					return a.coverage - b.coverage
				})[0]
			}

			const commonCandidates = jobRequiredTraits
				.filter(trait => (applicantTraits[trait] ?? 0) > 0)
				.map(toInterpret)
				.filter(Boolean)

			if (!commonCandidates.length) {
				return null
			}

			return commonCandidates.sort((a, b) => {
				if (a.coverage !== b.coverage) {
					return a.coverage - b.coverage
				}

				return b.jobPercent - a.jobPercent
			})[0]
		},
		overlappingTraits() {
			if (!this.matchData?.personality_v2) {
				return []
			}

			const {
				weights,
				normalized_applicant: applicant,
				normalized_job: job
			} = this.matchData.personality_v2

			const traitsToShow = Object.keys(weights).filter(
				trait => weights[trait] > 0
			)

			const applicantMissingTraits = traitsToShow
				.filter(trait => applicant[trait] === 0 && job[trait] > 0)
				.sort((a, b) => job[b] - job[a])

			const missingNormalizedResults = applicantMissingTraits.map(
				trait => ({
					trait,
					displayName: this.displayTraitName(trait),
					value: 0,
					emptyDots: 3,
					filledDots: 0
				})
			)

			const overlappingTraits = traitsToShow.filter(
				trait => !applicantMissingTraits.includes(trait)
			)

			const overlappingNormalizedResults = overlappingTraits
				.map(trait => {
					const rawValue =
						applicant[trait] / ((job[trait] ?? 0) || 1e-9)
					const value = Math.round(rawValue * 10000) / 10000

					let filledDots = 0
					const maxDots = 3

					if (value >= 1.2) {
						filledDots = 2
					} else if (value >= 0.8) {
						filledDots = 3
					} else if (value > 0) {
						filledDots = 1
					}

					return {
						trait,
						displayName: this.displayTraitName(trait),
						value,
						filledDots,
						emptyDots: maxDots - filledDots
					}
				})
				.sort((a, b) => a.value - b.value)
				.sort((a, b) => b.filledDots - a.filledDots)

			return [
				...overlappingNormalizedResults,
				...missingNormalizedResults
			]
		},
		comparisonKeyInsights() {
			if (!this.matchData?.personality_v2) {
				return {
					strengths: [],
					contributions: [],
					needsAttention: []
				}
			}

			const {
				normalized_applicant: applicant,
				normalized_job: job
			} = this.matchData.personality_v2

			const strengths = []
			const contributions = []
			const needsAttention = []

			TRAITS.forEach(trait => {
				const applicantValue = applicant[trait] ?? 0
				const jobValue = job[trait] ?? 0

				if (applicantValue > 0 && jobValue === 0) {
					contributions.push({
						trait,
						displayName: this.displayTraitName(trait),
						description: this.userIsApplicant
							? this.$t(
									`personality_results.applicant.${trait}.added-value`
							  )
							: this.$t(
									`personality_results.hiring_manager.${trait}.added-value`
							  ),
						applicantValue,
						jobValue
					})

					return
				}

				if (jobValue > 0) {
					const value =
						Math.round((applicantValue / jobValue) * 10000) / 10000
					const isStrength = value >= 0.8 && value <= 1.2

					let description

					if (isStrength) {
						description = this.$t(
							`personality_results.comparison.insights.${trait}.key-strength`
						)
					} else if (value <= GAP) {
						description = this.$t(
							`personality_results.trait_gap.${trait}.gap.where_theyll_struggle`
						)
					} else {
						description = this.$t(
							`personality_results.trait_gap.${trait}.borderline.where_theyll_struggle`
						)
					}

					const item = {
						trait,
						displayName: this.displayTraitName(trait),
						description,
						applicantValue,
						jobValue,
						value
					}

					if (isStrength) {
						strengths.push(item)
					} else {
						needsAttention.push(item)
					}
				}
			})

			return {
				strengths: strengths.sort((a, b) => b.value - a.value),
				contributions: contributions.sort(
					(a, b) => b.applicantValue - a.applicantValue
				),
				needsAttention: needsAttention.sort((a, b) => {
					const aGapFromIdeal = Math.abs(1 - a.value)
					const bGapFromIdeal = Math.abs(1 - b.value)

					return bGapFromIdeal - aGapFromIdeal
				})
			}
		},
		topTraits() {
			if (!this.personalityBreakdown) {
				return []
			}

			return getTopTraits(this.personalityBreakdown, 3)
		},
		topTraitsDisplay() {
			return this.topTraits.map(({ trait, percent }) => {
				const nameKey = getTraitName(trait)
				const workplaceSignalKey = getTraitWorkplaceSignal(trait)
				const signalDescriptionKey = getTraitSignalDescription(trait)
				const descriptionKey = `personality_results.trait.${trait}.description`
				const keyStrengthsKeys = [
					`personality_results.trait.${trait}.key_strengths.1`,
					`personality_results.trait.${trait}.key_strengths.2`,
					`personality_results.trait.${trait}.key_strengths.3`
				]
				const workStyleKeys = [
					`personality_results.trait.${trait}.work_style.1`,
					`personality_results.trait.${trait}.work_style.2`,
					`personality_results.trait.${trait}.work_style.3`
				]

				return {
					trait,
					percent,
					nameKey,
					workplaceSignalKey,
					signalDescriptionKey,
					descriptionKey,
					keyStrengthsKeys,
					workStyleKeys
				}
			})
		},
		allTraitsDisplay() {
			return TRAITS.map(trait => {
				const nameKey = getTraitName(trait)
				const displayName = this.displayTraitName(trait)
				const workplaceSignalKey = getTraitWorkplaceSignal(trait)
				const sloganKey = `personality_results.trait.${trait}.slogan`
				const descriptionKey = `personality_results.trait.${trait}.description`
				const keyStrengthsKeys = [
					`personality_results.trait.${trait}.key_strengths.1`,
					`personality_results.trait.${trait}.key_strengths.2`,
					`personality_results.trait.${trait}.key_strengths.3`
				]
				const workStyleKeys = [
					`personality_results.trait.${trait}.work_style.1`,
					`personality_results.trait.${trait}.work_style.2`,
					`personality_results.trait.${trait}.work_style.3`
				]

				return {
					trait,
					nameKey,
					displayName,
					workplaceSignalKey,
					sloganKey,
					descriptionKey,
					keyStrengthsKeys,
					workStyleKeys
				}
			})
		},
		summaryDisplay() {
			const profileDisplay = getProfileDisplayWithWrapper(
				this.personalityBreakdown,
				this.userIsApplicant ? 'applicant' : 'hiring_manager',
				// Prefer the canonical backend branch metadata (APR-1297) so
				// live API payloads route identically to fresh data; cached
				// payloads without the field fall back to local computation.
				this.activePersonalityData?.personality_classification || null
			)

			return {
				workplaceSignalKey: profileDisplay?.workplaceSignal || '',
				signalDescriptionKey: profileDisplay?.signalDescription || '',
				approach: profileDisplay?.approach
			}
		},
		topTraitDisplay() {
			return (
				this.topTraitsDisplay[0] || {
					keyStrengthsKeys: [],
					workStyleKeys: []
				}
			)
		},
		profileByRole() {
			const profileOverride =
				this.activePersonalityData?.personality_classification || null

			return {
				applicant: getProfileDisplayWithWrapper(
					this.personalityBreakdown,
					'applicant',
					profileOverride
				),
				hiring_manager: getProfileDisplayWithWrapper(
					this.personalityBreakdown,
					'hiring_manager',
					profileOverride
				)
			}
		},
		applicantInsights() {
			return this.pickInsights('applicant')
		},
		hiringManagerInsights() {
			return this.pickInsights('hiring_manager')
		},
		activeMode() {
			return this.wheelMode === 'comparison'
				? this.selectedMode
				: this.wheelMode
		},
		mode() {
			return this.activeMode
		},
		t() {
			return this.$t.bind(this)
		}
	},
	watch: {
		wheelMode: {
			immediate: true,
			handler(next) {
				if (next === 'comparison') {
					this.selectedMode = 'comparison'
				} else {
					this.selectedMode = next
				}
			}
		}
	},
	methods: {
		displayTraitName(trait) {
			if (!trait) {
				return ''
			}

			return TRAIT_LABELS[trait]
		},
		displayTraitSlogan(trait) {
			if (!trait) {
				return ''
			}

			return `personality_results.trait.${trait}.workplace_signal`
		},
		interpretV2Coverages(coverages, weights) {
			// Convert backend V2 coverages into UI-friendly format
			// with severity labels, gap identification, etc.
			const gaps = []
			const strengths = []
			const borderline = []

			Object.entries(coverages).forEach(([trait, coverage]) => {
				if (weights[trait] === 0) return // Skip traits job doesn't require

				if (coverage < GAP) {
					gaps.push({
						trait,
						coverage,
						severity: 'gap',
						percentage: Math.round(coverage * 100)
					})
				} else if (coverage < BORDERLINE) {
					borderline.push({
						trait,
						coverage,
						severity: 'borderline',
						percentage: Math.round(coverage * 100)
					})
				} else if (coverage >= V2_STRENGTH) {
					strengths.push({
						trait,
						coverage,
						severity: 'strength',
						percentage: Math.round(coverage * 100)
					})
				}
			})

			return {
				gaps,
				strengths,
				borderline,
				totalGaps: gaps.length,
				totalStrengths: strengths.length
			}
		},
		pickInsights(viewType) {
			const sectionTitles =
				this.profileByRole?.[viewType]?.languageWrapper.sectionTitles ||
				{}
			const insights = this.profileByRole?.[viewType]?.insights || {}

			return INSIGHT_KEYS.reduce((acc, key) => {
				const insightValue = insights[key]
				let insightKeys = []

				if (Array.isArray(insightValue)) {
					insightKeys = insightValue
				} else if (insightValue) {
					insightKeys = [insightValue]
				}

				acc[key] = {
					titleKey: sectionTitles[key] || '',
					insightKeys
				}
				return acc
			}, {})
		},
		buildPersonalityObject(data) {
			const personality = Array.isArray(data?.personality)
				? data.personality
				: []

			if (personality.length === 0) {
				return {}
			}

			const personalities = personality.reduce((acc, curr) => {
				acc[curr] = (acc[curr] || 0) + 1
				return acc
			}, {})

			const total = personality.length

			const scores = Object.fromEntries(
				Object.entries(personalities).map(([trait, count]) => [
					trait,
					Math.round((count / total) * 100)
				])
			)

			return scores
		}
	}
}
